import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API_BASE, getToken } from "@/lib/api";
import { toast } from "sonner";
import {
  Heart, Baby, Calendar, BookOpen, Camera, Envelope, Notebook, Sparkle,
  ChatCircleDots, Crown, Pulse, Pill, FileText, Plant, Hospital, Users,
  ChatsCircle, ListChecks, Person, UsersThree, Egg, MoonStars, Syringe,
  DownloadSimple, ShareNetwork, Copy, X,
} from "@phosphor-icons/react";

export default function Journey() {
  const [data, setData] = useState(null);
  const [memories, setMemories] = useState([]);
  const [share, setShare] = useState(null);
  const [shareModal, setShareModal] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const token = getToken();

  useEffect(() => {
    api.get("/journey/summary").then((r) => setData(r.data));
    api.get("/memories").then((r) => setMemories(r.data));
  }, []);

  const openShare = async () => {
    setShareModal(true);
    if (share) return;
    try {
      const r = await api.post("/share/journey");
      setShare(r.data);
    } catch {
      toast.error("Could not create share link.");
    }
  };

  const revokeShare = async () => {
    try {
      await api.delete("/share/journey");
      setShare(null);
      setShareModal(false);
      toast.success("Share link revoked.");
    } catch {
      toast.error("Could not revoke.");
    }
  };

  const copyShareUrl = () => {
    const url = shareUrl();
    navigator.clipboard?.writeText(url);
    toast.success("Link copied.");
  };

  const shareUrl = () => share ? `${window.location.origin}/share/${share.code}` : "";

  const downloadYearbook = async () => {
    setDownloading(true);
    try {
      const r = await api.get("/yearbook/generate", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "vansha-yearbook.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Yearbook downloaded.");
    } catch {
      toast.error("Could not generate yearbook.");
    } finally { setDownloading(false); }
  };

  if (!data) return <div className="text-ink-500">Loading your journey…</div>;

  const c = data.counts;
  const f = data.family || {};
  const status = data.pregnancy_status;
  const name = data.user?.name?.split(" ")[0] || "Friend";
  const memoryUrl = (id) => `${API_BASE}/memories/file/${id}?auth=${token}`;
  const heroMemories = memories.slice(0, 6);

  const sections = [
    {
      title: "The pregnancy heart",
      blurb: "Where Vansha begins — week-by-week intelligence for an active pregnancy.",
      items: [
        { icon: Heart, label: "Week-by-week tracker", to: "/app/pregnancy",
          line: status ? `${name} is in week ${status.current_week}, trimester ${status.trimester}. ${status.days_to_due} days to baby.` : "Tracks all 40 weeks." },
        { icon: Pulse, label: "Symptom tracker", to: "/app/symptoms",
          line: `${c.symptoms} daily logs (mood, sleep, weight, BP, kicks). Trend charts.` },
        { icon: Pill, label: "Medications", to: "/app/medications",
          line: `${c.medications} medications tracked · ${c.doses} doses logged.` },
        { icon: Plant, label: "Nutrition", to: "/app/nutrition",
          line: `${c.nutrition} days of meals, water, supplements logged.` },
        { icon: FileText, label: "Medical records vault", to: "/app/records",
          line: `${c.records} medical documents stored.` },
        { icon: Calendar, label: "Appointments", to: "/app/appointments",
          line: `${c.appointments} appointments (scans, doctor visits, classes).` },
      ],
    },
    {
      title: "Delivery readiness",
      blurb: "Everything ready, so when the moment comes you can simply breathe.",
      items: [
        { icon: Hospital, label: "Hospital readiness center", to: "/app/delivery-prep",
          line: `${data.hospital_bag_checked}/20 items packed${data.has_birth_plan ? " · birth plan filled" : ""} · ${c.contacts} contacts.` },
        { icon: Egg, label: "Pre-conception (cycles)", to: "/app/preconception",
          line: `${c.cycles} cycle${c.cycles === 1 ? "" : "s"} logged with fertility window.` },
      ],
    },
    {
      title: "Baby",
      blurb: "From digital twin to vaccinations — the next chapter is ready when you are.",
      items: [
        { icon: Baby, label: "Baby digital twin", to: "/app/baby",
          line: data.has_baby ? "Baby profile created." : "Profile activates after birth." },
        { icon: Syringe, label: "Vaccinations (IAP)", to: "/app/vaccinations",
          line: `${c.vaccinations} vaccines logged · 12-entry IAP schedule.` },
        { icon: MoonStars, label: "Newborn dashboard", to: "/app/newborn",
          line: `${c.newborn_logs} newborn logs. Activates day baby arrives.` },
      ],
    },
    {
      title: "Memory & legacy",
      blurb: "Everything captured, sealed, and one day printed.",
      items: [
        { icon: Sparkle, label: "Timeline", to: "/app/timeline",
          line: `${c.timeline} timeline events — every milestone and wonder.` },
        { icon: Camera, label: "Memory vault", to: "/app/memories",
          line: `${c.memories} photos in the object-storage vault.`, memoryPreview: true },
        { icon: Envelope, label: "Letters to baby", to: "/app/letters",
          line: `${c.letters} letters, some sealed as time-capsules.` },
        { icon: Notebook, label: "Journal", to: "/app/journal",
          line: `${c.journal} journal entries, prompted daily.` },
        { icon: BookOpen, label: "Yearbook PDF", to: "/app/yearbook",
          line: "Auto-curated yearbook — every photo, letter & milestone." },
      ],
    },
    {
      title: "Family & community",
      blurb: "Never alone — invite the ones who matter, and find your people.",
      items: [
        { icon: Users, label: "Family circle", to: "/app/family",
          line: `${f.members || 1} family member${(f.members || 1) === 1 ? "" : "s"}.` },
        { icon: ChatsCircle, label: "Family feed", to: "/app/feed",
          line: `${f.posts || 0} private posts with reactions + comments.` },
        { icon: ListChecks, label: "Family tasks", to: "/app/tasks",
          line: `${f.tasks_done || 0}/${f.tasks || 0} family tasks completed.` },
        { icon: Person, label: "Father mode", to: "/app/father",
          line: "A separate sacred journey with weekly guidance." },
        { icon: UsersThree, label: "Grandparent mode", to: "/app/grandparent",
          line: "Modern wisdom + how-to-help checklist." },
        { icon: ChatsCircle, label: "Community", to: "/app/community",
          line: `${c.community_posts} posts across 8 moderated groups.` },
      ],
    },
    {
      title: "Daily companion",
      blurb: "Why families open Vansha every single day.",
      items: [
        { icon: ChatCircleDots, label: "AI Assistant (6 personas)", to: "/app/assistant",
          line: `${c.assistant_msgs} messages with Claude Sonnet 4.5.` },
        { icon: Sparkle, label: "Check-ins & smart prompts", to: "/app/dashboard",
          line: `${c.checkins} daily check-ins. Prompts adapt to week + state.` },
        { icon: BookOpen, label: "Knowledge Academy", to: "/app/academy",
          line: "6 doctor-style courses across pregnancy + parenting." },
        { icon: Crown, label: "Membership", to: "/app/membership",
          line: `On ${data.user.membership_tier?.toUpperCase()} tier. 4 tiers available.` },
      ],
    },
  ];

  return (
    <div className="space-y-10" data-testid="journey-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="overline text-saffron mb-2">{name}'s journey · feature tour</p>
          <h1 className="font-serif font-semibold text-4xl sm:text-5xl text-ink-900">
            What <em className="text-terracotta">{name}</em> has done<br />on Vansha so far.
          </h1>
          <p className="text-ink-700 mt-3 max-w-2xl">A complete walkthrough of every Vansha feature, narrated through one family's pregnancy. Click any card to jump in.</p>
        </div>
        <button onClick={openShare} className="btn-secondary" data-testid="share-journey-btn">
          <ShareNetwork weight="thin" size={18} /> Share this journey
        </button>
      </header>

      {heroMemories.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3" data-testid="journey-hero-strip">
          {heroMemories.map((m) => (
            <div key={m.id} className="aspect-square rounded-2xl overflow-hidden bg-vansha-accent" data-testid={`hero-memory-${m.id}`}>
              <img src={memoryUrl(m.id)} alt={m.caption || ""} className="w-full h-full object-cover" loading="lazy" />
            </div>
          ))}
        </div>
      )}

      <div className="vansha-card p-8 sm:p-10 bg-forest text-white" data-testid="journey-headline">
        <p className="overline" style={{ color: "#D9C9B4" }}>By the numbers</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-6 mt-6">
          <Big v={c.timeline} l="Timeline events" />
          <Big v={c.memories} l="Photos" />
          <Big v={c.letters} l="Letters" />
          <Big v={c.journal} l="Journal entries" />
          <Big v={c.symptoms} l="Symptom logs" />
          <Big v={c.medications} l="Medications" />
          <Big v={c.appointments} l="Appointments" />
          <Big v={c.nutrition} l="Nutrition days" />
          <Big v={data.hospital_bag_checked} l="Bag items packed" />
          <Big v={f.posts || 0} l="Family posts" />
          <Big v={c.assistant_msgs} l="AI messages" />
          <Big v={c.checkins} l="Check-ins" />
        </div>
      </div>

      {sections.map((s, i) => (
        <section key={i} data-testid={`section-${i}`}>
          <p className="overline text-saffron mb-2">Chapter {i + 1}</p>
          <h2 className="font-serif text-3xl">{s.title}</h2>
          <p className="text-ink-700 mt-2 mb-6 max-w-2xl">{s.blurb}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {s.items.map((it, j) => (
              <Link key={j} to={it.to} className="vansha-card p-6 hover:shadow-lg hover:-translate-y-1 transition-all overflow-hidden" data-testid={`feature-${it.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                <it.icon weight="thin" size={26} className="text-terracotta mb-3" />
                <h3 className="font-serif text-xl leading-tight">{it.label}</h3>
                <p className="text-sm text-ink-700 mt-2">{it.line}</p>
                {it.memoryPreview && memories.length > 0 && (
                  <div className="flex gap-1.5 mt-4" data-testid="memory-preview-strip">
                    {memories.slice(0, 4).map((m) => (
                      <div key={m.id} className="w-1/4 aspect-square rounded-lg overflow-hidden bg-vansha-accent">
                        <img src={memoryUrl(m.id)} alt="" className="w-full h-full object-cover" loading="lazy" />
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-sm text-terracotta mt-3 font-medium">Open →</p>
              </Link>
            ))}
          </div>
        </section>
      ))}

      {/* Emotional finale — download yearbook CTA */}
      <div className="vansha-card p-8 sm:p-14 bg-forest text-white relative overflow-hidden" data-testid="journey-finale">
        <div className="absolute -right-20 -top-20 w-96 h-96 rounded-full bg-saffron/10 blur-3xl" />
        <div className="relative max-w-3xl">
          <p className="overline mb-4" style={{ color: "#D9C9B4" }}>The story so far</p>
          <h2 className="font-serif font-semibold text-4xl sm:text-5xl leading-tight">
            Every moment. Every letter.<br />
            <em className="text-saffron">Bound into one book.</em>
          </h2>
          <p className="mt-6 text-lg opacity-85 leading-relaxed">
            {name}'s Vansha yearbook is <b>{c.timeline}</b> timeline moments · <b>{c.letters}</b> letters · <b>{c.journal}</b> journal entries · <b>{c.memories}</b> photographs — auto-curated into a beautifully typeset PDF, ready to be printed and treasured.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <button onClick={downloadYearbook} disabled={downloading}
              className="bg-saffron text-forest hover:bg-[#b87a39] rounded-full px-8 py-4 font-medium transition inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="finale-download-yearbook">
              <DownloadSimple weight="thin" size={20} />
              {downloading ? "Preparing your yearbook…" : "Download your yearbook PDF"}
            </button>
            <button onClick={openShare} className="border border-white/30 text-white rounded-full px-8 py-4 font-medium hover:border-white transition inline-flex items-center gap-2" data-testid="finale-share">
              <ShareNetwork weight="thin" size={20} /> Share with family
            </button>
          </div>
          <p className="mt-6 text-sm opacity-70">Signature members receive a printed hardcover edition each year.</p>
        </div>
      </div>

      <div className="vansha-card p-8 sm:p-10" data-testid="journey-takeaway">
        <p className="overline text-saffron mb-2">The takeaway</p>
        <p className="font-serif text-2xl sm:text-3xl leading-snug">
          Vansha is <em className="text-terracotta">not an app</em> — it's the operating system of a family.
        </p>
        <p className="text-ink-700 mt-4">One login. Every feature. From the first heartbeat to the first steps and beyond.</p>
      </div>

      {shareModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShareModal(false)} data-testid="share-modal">
          <div className="bg-vansha max-w-lg w-full rounded-3xl p-8 sm:p-10 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShareModal(false)} className="absolute top-4 right-4 btn-ghost p-2" data-testid="close-share"><X weight="thin" size={20} /></button>
            <p className="overline text-saffron mb-2">Share</p>
            <h2 className="font-serif text-3xl mb-3">Share your journey</h2>
            <p className="text-ink-700 mb-6">A curated read-only page — timeline highlights, photos and letter titles. Perfect for extended family. No login required. Journal entries and medical data stay private.</p>
            {share ? (
              <>
                <div className="vansha-card p-4 flex items-center gap-3 mb-4 bg-vansha-accent" data-testid="share-url">
                  <input readOnly value={shareUrl()} className="input-line flex-1 font-mono text-sm" onFocus={(e) => e.target.select()} data-testid="share-url-input" />
                  <button onClick={copyShareUrl} className="btn-primary text-sm flex-shrink-0" data-testid="copy-share-url"><Copy weight="thin" size={14} /> Copy</button>
                </div>
                <a href={shareUrl()} target="_blank" rel="noreferrer" className="text-sm text-terracotta underline-offset-2 hover:underline" data-testid="preview-share">Preview →</a>
                <div className="mt-8 pt-4 border-t border-ink-10">
                  <button onClick={revokeShare} className="text-sm text-ink-500 hover:text-terracotta" data-testid="revoke-share">Revoke this link</button>
                </div>
              </>
            ) : (
              <p className="text-ink-500">Creating your share link…</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Big({ v, l }) {
  return (
    <div>
      <p className="font-serif text-4xl text-saffron">{v}</p>
      <p className="overline mt-1" style={{ color: "#D9C9B4" }}>{l}</p>
    </div>
  );
}
